import { Box, Button, FormField, Input, Text } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useRef, useState } from 'react';
import { EditorContext, ModalContext, PluginsEventsContext, RicosContext } from 'ricos-context';
import type { EmbedData } from 'ricos-schema';
import { containsUrl, EMBED_TYPE, MEDIA_POPOVERS_BUTTONS_NAMES_BI } from 'wix-rich-content-common';
import { FloatingLayout } from 'wix-rich-content-ui-components';

import { socialModals } from '../consts';
import {
  SOCIAL_EMBED_MODAL,
  SOCIAL_EMBED_MODAL_INPUT,
  SOCIAL_EMBED_MODAL_SUBMIT_BTN,
} from '../dataHooks';
import { EMBED_DEFAULTS } from '../defaults';
import type { LinkPreviewPluginEditorConfig } from '../types';
import { LINK_PREVIEW_TYPE } from '../types';

export type InsertModalProps = {
  fetchData: LinkPreviewPluginEditorConfig['fetchData'];
  socialType: 'Instagram' | 'Twitter' | 'Pinterest' | 'Facebook' | 'TikTok';
  componentData: EmbedData;
  nodeId?: string;
};

export const InsertModal: FC<InsertModalProps> = ({
  componentData,
  fetchData,
  nodeId,
  socialType,
}) => {
  const { t, isMobile } = useContext(RicosContext);
  const { getEditorCommands } = useContext(EditorContext);
  const modalService = useContext(ModalContext) || {};
  const pluginsEvents = useContext(PluginsEventsContext);

  const [url, setUrl] = useState('');
  const [submittedInvalidUrl, setSubmittedInvalidUrl] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pluginEvents = {
    onPluginsPopOverClick: data => pluginsEvents.publishPluginPopoverClick(data),
  };

  const closeModal = () => {
    setSubmittedInvalidUrl(false);
    modalService.closeModal(socialModals[socialType.toLowerCase()]);
  };

  const onConfirm = (embedData: EmbedData) => {
    const editorCommands = getEditorCommands();
    if (nodeId) {
      editorCommands?.setBlock(nodeId, EMBED_TYPE, embedData);
    } else {
      editorCommands?.insertBlockWithBlankLines(EMBED_TYPE, embedData);
    }
  };

  const handleOnConfirm = async () => {
    pluginEvents?.onPluginsPopOverClick?.({
      pluginId: LINK_PREVIEW_TYPE,
      buttonName: MEDIA_POPOVERS_BUTTONS_NAMES_BI.embed,
    });
    if (url) {
      try {
        const { html } = (await fetchData?.(url)) || {};
        if (!html) {
          setSubmittedInvalidUrl(true);
          inputRef.current?.focus();
          return;
        }
        onConfirm({
          ...componentData,
          ...EMBED_DEFAULTS,
          src: url,
          oembed: {
            type: 'rich',
            html,
          },
        });

        closeModal();
      } catch {
        setSubmittedInvalidUrl(true);
        inputRef.current?.focus();
      }
    } else {
      setSubmittedInvalidUrl(true);
      inputRef.current?.focus();
    }
  };

  const onCloseRequested = () => {
    pluginEvents?.onPluginsPopOverClick?.({
      pluginId: LINK_PREVIEW_TYPE,
      buttonName: MEDIA_POPOVERS_BUTTONS_NAMES_BI.cancel,
    });
    closeModal();
  };

  return (
    <FloatingLayout
      onCloseButtonClick={onCloseRequested}
      dataHook={SOCIAL_EMBED_MODAL}
      header={isMobile ? { title: 'Embed' } : undefined}
      content={{
        noPadding: true,
        children: (
          <Box direction="vertical" gap="SP3" padding="SP4">
            <FormField
              label={
                <Text size="tiny" weight="thin">
                  {t(`EmbedURL_Social_${socialType}_Title`)}
                </Text>
              }
              status={submittedInvalidUrl ? 'error' : undefined}
              statusMessage={
                submittedInvalidUrl ? t('SoundCloudUploadModal_Input_InvalidUrl') : undefined
              }
            >
              <Input
                inputRef={ref => {
                  inputRef.current = ref;
                }}
                type="url"
                id="dropdown-text-input"
                autocomplete="off"
                value={url}
                dataHook={SOCIAL_EMBED_MODAL_INPUT}
                onChange={e => {
                  setSubmittedInvalidUrl(false);

                  setUrl(e.target.value);
                }}
                placeholder={t(`EmbedURL_Social_${socialType}_Placeholder`)}
                onBlur={() => {
                  if (url && !containsUrl(url)) {
                    setSubmittedInvalidUrl(true);
                  }
                }}
              />
            </FormField>
            <Button dataHook={SOCIAL_EMBED_MODAL_SUBMIT_BTN} onClick={handleOnConfirm}>
              {t('Embed_Add_Button_Label')}
            </Button>
          </Box>
        ),
      }}
    />
  );
};
